import { yupResolver } from "@hookform/resolvers/yup";
import CloseIcon from "@mui/icons-material/Close";
import LoadingButton from "@mui/lab/LoadingButton";
import { Autocomplete } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import defaultAxios, { AxiosError } from "axios";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import NumberFormat from "react-number-format";
import { useMutation, useQueryClient } from "react-query";
import Swal from "sweetalert2";
import * as yup from "yup";
import {
    AddRoomCoWorkKhusus,
    ErrorFieldResponse,
    IDataLanggananMember,
} from "../../constants/types";
import { useAuth } from "../../context/authContext";
import axios from "../../services/axios";

interface IModalTambahRuanganProps {
    isOpenModalTambahRuanganKhusus: boolean;
    closeModalTambahRuanganKhusus: () => void;
    dataLanggananMember?: IDataLanggananMember;
}

type Body = {
    ukmId: number | undefined;
    nama: string;
    kapasitas: number;
    jenisSewa: string;
    hargaSewaMember: string;
    hargaSewaNonMember: string;
    hargaSewaBulananMember?: string;
    hargaSewaBulananNonMember?: string;
};

const schema = yup.object({
    nama: yup
        .string()
        .required("Kolom wajib diisi")
        .max(50, "Nama ruangan terlalu panjang"),
    kapasitas: yup
        .number()
        .typeError("Kapasitas harus berupa angka")
        .required("Kapasitas wajib diisi")
        .min(1, "Kapasitas minimal 1"),
    jenisSewa: yup.string().required("Jenis Sewa wajib dipilih"),
    hargaSewaMember: yup.string().when("jenisSewa", {
        is: (jenisSewa: string) =>
            jenisSewa === "Jam" || jenisSewa === "Mingguan",
        then: yup.string().required("Harga sewa member wajib diisi"),
        otherwise: yup.string().notRequired(),
    }),
    hargaSewaNonMember: yup.string().when("jenisSewa", {
        is: (jenisSewa: string) =>
            jenisSewa === "Jam" || jenisSewa === "Mingguan",
        then: yup.string().required("Harga sewa non-member wajib diisi"),
        otherwise: yup.string().notRequired(),
    }),
    hargaSewaBulananMember: yup.string().when("jenisSewa", {
        is: "Bulanan",
        then: yup.string().required("Harga sewa bulanan member wajib diisi"),
        otherwise: yup.string().notRequired(),
    }),
    hargaSewaBulananNonMember: yup.string().when("jenisSewa", {
        is: "Bulanan",
        then: yup
            .string()
            .required("Harga sewa bulanan non-member wajib diisi"),
        otherwise: yup.string().notRequired(),
    }),
});

const ModalTambahRuangan = ({
    isOpenModalTambahRuanganKhusus,
    closeModalTambahRuanganKhusus,
    dataLanggananMember,
}: IModalTambahRuanganProps) => {
    const theme = useTheme();
    const isPhoneScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const [isButtonLoading, setIsButtonLoading] = useState(false);

    const { ukmIdUser } = useAuth();

    const initialValues: AddRoomCoWorkKhusus = React.useMemo(
        () => ({
            id: 0,
            ukmId: ukmIdUser && ukmIdUser[0],
            nama: "",
            kapasitas: 0,
            jenisSewa: "bulanan",
            hargaSewaMember: "",
            hargaSewaNonMember: "",
            hargaSewaBulananMember: "",
            hargaSewaBulananNonMember: "",
        }),
        [ukmIdUser],
    );

    const {
        handleSubmit,
        control,
        formState: { errors },
        setError,
        setValue,
        clearErrors,
        reset,
        watch,
    } = useForm<AddRoomCoWorkKhusus>({
        resolver: yupResolver(schema),
        defaultValues: initialValues,
    });

    const selectedJenisSewa = watch("jenisSewa") || "Bulanan";

    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: async (values: {
            body: Body;
            isEdit: boolean;
            id?: number;
        }) => {
            const { body, isEdit, id } = values;

            const formData = new FormData();
            if (body.ukmId !== undefined) {
                formData.append("ukmId", String(body.ukmId));
            }
            formData.append("nama", body.nama);
            formData.append("kapasitas", String(body.kapasitas));
            formData.append("jenisSewa", String(body.jenisSewa.toLowerCase()));

            // Add price fields based on jenisSewa
            if (
                body.jenisSewa.toLowerCase() === "jam" ||
                body.jenisSewa.toLowerCase() === "mingguan"
            ) {
                formData.append("hargaSewaMember", body.hargaSewaMember);
                formData.append("hargaSewaNonMember", body.hargaSewaNonMember);
            } else if (body.jenisSewa.toLowerCase() === "bulanan") {
                formData.append(
                    "hargaSewaBulananMember",
                    body.hargaSewaBulananMember || "",
                );
                formData.append(
                    "hargaSewaBulananNonMember",
                    body.hargaSewaBulananNonMember || "",
                );
            }

            if (isEdit && id) {
                formData.append("_method", "put");
                const res = await axios.post(
                    `/api/mruangans-khusus/${id}`,
                    formData,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    },
                );
                return res.data;
            } else {
                const res = await axios.post(
                    `/api/mruangans-khusus`,
                    formData,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    },
                );
                return res.data;
            }
        },
    });

    const onSubmit = (values: AddRoomCoWorkKhusus) => {
        setIsButtonLoading(true);

        // Format prices based on jenisSewa
        let body: Body = {
            ukmId: values.ukmId,
            nama: values.nama,
            kapasitas: values.kapasitas,
            jenisSewa: values.jenisSewa,
            hargaSewaMember: "",
            hargaSewaNonMember: "",
        };

        if (
            values.jenisSewa.toLowerCase() === "jam" ||
            values.jenisSewa.toLowerCase() === "mingguan"
        ) {
            body.hargaSewaMember = String(values.hargaSewaMember).replace(
                /\./g,
                "",
            );
            body.hargaSewaNonMember = String(values.hargaSewaNonMember).replace(
                /\./g,
                "",
            );
        } else if (values.jenisSewa.toLowerCase() === "bulanan") {
            body.hargaSewaBulananMember = String(
                values.hargaSewaBulananMember,
            ).replace(/\./g, "");
            body.hargaSewaBulananNonMember = String(
                values.hargaSewaBulananNonMember,
            ).replace(/\./g, "");
        }

        mutation.mutate(
            {
                body,
                isEdit: !!dataLanggananMember,
                id: dataLanggananMember?.id,
            },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries("listRoom");
                    Swal.fire({
                        title: dataLanggananMember
                            ? "Ruangan berhasil diubah"
                            : "Ruangan berhasil ditambahkan",
                        position: "top-end",
                        showConfirmButton: false,
                        icon: "success",
                        toast: true,
                        timer: 3000,
                        timerProgressBar: true,
                        showCloseButton: true,
                        customClass: {
                            container: "my-swal",
                        },
                    });
                    closeModalTambahRuanganKhusus();
                    reset(initialValues);
                    setIsButtonLoading(false);
                },
                onError: (error) => {
                    if (defaultAxios.isAxiosError(error)) {
                        const serverError = error as AxiosError<
                            ErrorFieldResponse | undefined
                        >;
                        if (serverError && serverError?.response) {
                            const fieldError = serverError?.response?.data;
                            if (fieldError?.errors) {
                                Object.keys(fieldError.errors).forEach(
                                    (key) => {
                                        const errorMessage =
                                            fieldError.errors[key];
                                        setError(
                                            key as keyof typeof initialValues,
                                            {
                                                type: "manual",
                                                message: errorMessage[0],
                                            },
                                        );
                                    },
                                );
                            } else {
                                Swal.fire({
                                    title: `${
                                        fieldError?.message ??
                                        "Terjadi Kesalahan!"
                                    }`,
                                    position: "top-end",
                                    showConfirmButton: false,
                                    icon: "error",
                                    toast: true,
                                    timer: 3000,
                                    timerProgressBar: true,
                                    showCloseButton: true,
                                    customClass: {
                                        container: "my-swal",
                                    },
                                });
                            }
                        }
                    }
                    console.error(error);
                    setIsButtonLoading(false);
                },
            },
        );
    };

    useEffect(() => {
        if (isOpenModalTambahRuanganKhusus) {
            if (dataLanggananMember) {
                reset({
                    ...dataLanggananMember,
                });
            } else {
                reset({
                    ...initialValues,
                    jenisSewa: "Bulanan",
                });
            }
        }
    }, [
        reset,
        initialValues,
        isOpenModalTambahRuanganKhusus,
        dataLanggananMember,
    ]);

    const getHargaLabel = () => {
        switch (selectedJenisSewa) {
            case "Jam":
                return "per Jam";
            case "Mingguan":
                return "per Minggu";
            case "Bulanan":
                return "per Bulan";
            default:
                return "";
        }
    };

    const renderHargaFields = () => {
        if (selectedJenisSewa === "Bulanan") {
            return (
                <>
                    <Grid item xs={6}>
                        <Typography mb={1}>
                            Harga Sewa Member ({getHargaLabel()})
                        </Typography>
                        <Controller
                            name={"hargaSewaBulananMember"}
                            control={control}
                            render={({ field, fieldState }) => (
                                <NumberFormat
                                    customInput={TextField}
                                    placeholder="Contoh: 1.500.000"
                                    thousandSeparator="."
                                    decimalSeparator=","
                                    fullWidth
                                    variant="outlined"
                                    size="medium"
                                    error={!!fieldState?.error}
                                    helperText={
                                        fieldState?.error
                                            ? fieldState?.error.message
                                            : " "
                                    }
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Typography>Rp.</Typography>
                                            </InputAdornment>
                                        ),
                                    }}
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Typography mb={1}>
                            Harga Sewa Non Member ({getHargaLabel()})
                        </Typography>
                        <Controller
                            name={"hargaSewaBulananNonMember"}
                            control={control}
                            render={({ field, fieldState }) => (
                                <NumberFormat
                                    customInput={TextField}
                                    placeholder="Contoh: 2.000.000"
                                    thousandSeparator="."
                                    decimalSeparator=","
                                    fullWidth
                                    variant="outlined"
                                    size="medium"
                                    error={!!fieldState?.error}
                                    helperText={
                                        fieldState?.error
                                            ? fieldState?.error.message
                                            : " "
                                    }
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Typography>Rp.</Typography>
                                            </InputAdornment>
                                        ),
                                    }}
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                </>
            );
        } else {
            return (
                <>
                    <Grid item xs={6}>
                        <Typography mb={1}>
                            Harga Sewa Member ({getHargaLabel()})
                        </Typography>
                        <Controller
                            name={"hargaSewaMember"}
                            control={control}
                            render={({ field, fieldState }) => (
                                <NumberFormat
                                    customInput={TextField}
                                    placeholder={
                                        selectedJenisSewa === "Jam"
                                            ? "Contoh: 50.000"
                                            : "Contoh: 350.000"
                                    }
                                    thousandSeparator="."
                                    decimalSeparator=","
                                    fullWidth
                                    variant="outlined"
                                    size="medium"
                                    error={!!fieldState?.error}
                                    helperText={
                                        fieldState?.error
                                            ? fieldState?.error.message
                                            : " "
                                    }
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Typography>Rp.</Typography>
                                            </InputAdornment>
                                        ),
                                    }}
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Typography mb={1}>
                            Harga Sewa Non Member ({getHargaLabel()})
                        </Typography>
                        <Controller
                            name={"hargaSewaNonMember"}
                            control={control}
                            render={({ field, fieldState }) => (
                                <NumberFormat
                                    customInput={TextField}
                                    placeholder={
                                        selectedJenisSewa === "Jam"
                                            ? "Contoh: 75.000"
                                            : "Contoh: 500.000"
                                    }
                                    thousandSeparator="."
                                    decimalSeparator=","
                                    fullWidth
                                    variant="outlined"
                                    size="medium"
                                    error={!!fieldState?.error}
                                    helperText={
                                        fieldState?.error
                                            ? fieldState?.error.message
                                            : " "
                                    }
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Typography>Rp.</Typography>
                                            </InputAdornment>
                                        ),
                                    }}
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                </>
            );
        }
    };

    return (
        <Dialog
            maxWidth="md"
            fullScreen={isPhoneScreen}
            fullWidth={true}
            open={isOpenModalTambahRuanganKhusus}
            onClose={() => {}}
            scroll="body"
            PaperProps={{
                sx: {
                    borderRadius: 1,
                },
            }}
        >
            <DialogTitle>
                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Typography variant="h6" fontWeight="bold">
                        Tambah Ruangan Khusus
                    </Typography>

                    <IconButton onClick={closeModalTambahRuanganKhusus}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent
                dividers
                sx={{ padding: isPhoneScreen ? undefined : 6 }}
            >
                <Stack gap={6}>
                    <Grid container alignItems="center" spacing={2}>
                        <Grid item xs={12}>
                            <Typography mb={1}>Nama Ruangan</Typography>

                            <Controller
                                name="nama"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        id="nama"
                                        inputProps={{
                                            "data-testid": "input-nama",
                                        }}
                                        placeholder="Masukan Nama Ruangan"
                                        fullWidth
                                        variant="outlined"
                                        error={Boolean(errors.nama)}
                                        helperText={
                                            errors.nama
                                                ? errors.nama.message
                                                : ""
                                        }
                                        {...field}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Typography mb={1}>Kapasitas Ruangan</Typography>

                            <Controller
                                name="kapasitas"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        id="kapasitas"
                                        inputProps={{
                                            "data-testid": "input-kapasitas",
                                        }}
                                        placeholder="Contoh: 20"
                                        fullWidth
                                        variant="outlined"
                                        error={Boolean(errors.kapasitas)}
                                        helperText={
                                            errors.kapasitas
                                                ? errors.kapasitas.message
                                                : ""
                                        }
                                        {...field}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={6} mt="20px">
                            <Typography mb={1}>Jenis Sewa</Typography>

                            <Autocomplete
                                options={["Bulanan", "Jam", "Mingguan"]}
                                getOptionLabel={(option) => option}
                                value={selectedJenisSewa}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        error={Boolean(errors.jenisSewa)}
                                        helperText={
                                            errors.jenisSewa
                                                ? errors.jenisSewa.message
                                                : " "
                                        }
                                    />
                                )}
                                onChange={(_, newValue) => {
                                    if (newValue) {
                                        setValue("jenisSewa", newValue);
                                        clearErrors("jenisSewa");

                                        // Clear all price fields when changing jenis sewa
                                        setValue("hargaSewaMember", "");
                                        setValue("hargaSewaNonMember", "");
                                        setValue("hargaSewaBulananMember", "");
                                        setValue(
                                            "hargaSewaBulananNonMember",
                                            "",
                                        );
                                    } else {
                                        setValue("jenisSewa", "");
                                    }
                                }}
                            />
                        </Grid>

                        {renderHargaFields()}
                    </Grid>

                    <Stack
                        direction="row"
                        spacing={1.5}
                        width="100%"
                        sx={{
                            justifyContent: "flex-end",
                            alignItems: "center",
                        }}
                    >
                        <LoadingButton
                            loadingPosition="center"
                            variant="outlined"
                            onClick={closeModalTambahRuanganKhusus}
                        >
                            Kembali
                        </LoadingButton>

                        <LoadingButton
                            loading={isButtonLoading}
                            loadingPosition="center"
                            variant="contained"
                            onClick={handleSubmit(onSubmit)}
                            disabled={isButtonLoading}
                            data-testid="simpan-ruangan"
                        >
                            Tambah
                        </LoadingButton>
                    </Stack>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

export default ModalTambahRuangan;
